import {
  Stack,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  Box,
} from '@mui/material';
import React, { useRef, useState, useEffect } from 'react';
import VisibilityIcon from '@mui/icons-material/Visibility';
import AllOutRoundedIcon from '@mui/icons-material/AllOutRounded';
import LocalHospitalIcon from '@mui/icons-material/LocalHospital';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';

import type { IntakeStatusNote } from '@shared/patient-intake-status';

import { CopyButton } from '../../../../components/ui/CopyButton.jsx';

type PatientVisibilityIconProps = {
  isVisibleToPatient: boolean;
};

const PatientVisibilityIcon = ({ isVisibleToPatient }: PatientVisibilityIconProps) => {
  if (isVisibleToPatient === undefined) {
    return null;
  }

  if (isVisibleToPatient) {
    return (
      <Tooltip title="This note is visible to the patient">
        <VisibilityIcon fontSize="small" color="inherit" />
      </Tooltip>
    );
  }

  return (
    <Tooltip title="This note is not visible to the patient">
      <VisibilityOffIcon fontSize="small" color="inherit" />
    </Tooltip>
  );
};

type MedicalNoteIconProps = {
  isMedicalNote: boolean;
};

const MedicalNoteIcon = ({ isMedicalNote }: MedicalNoteIconProps) => {
  if (!isMedicalNote) {
    return null;
  }

  return (
    <Tooltip title="This is a medical note">
      <LocalHospitalIcon fontSize="small" color="primary" />
    </Tooltip>
  );
};

interface NoteProps extends Omit<React.ComponentProps<typeof Stack>, 'visibility'> {
  visibility: IntakeStatusNote['visibility'] | null;
}

export const Note: React.FC<NoteProps> = ({ visibility, children, ...props }) => {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isOverflowing, setIsOverflowing] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const checkOverflow = () => {
      if (!contentRef.current) {
        return;
      }

      const element = contentRef.current;
      setIsOverflowing(element.scrollHeight > element.clientHeight);
    };

    // Initial check
    checkOverflow();

    const resizeObserver = new ResizeObserver(checkOverflow);

    if (contentRef.current) {
      resizeObserver.observe(contentRef.current);
    }

    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  const handleOpenDialog = (e: React.MouseEvent<HTMLButtonElement | HTMLDivElement>) => {
    // If the note is not overflowing, don't open the dialog and propagate the click event
    if (!isOverflowing) {
      return;
    }

    e.stopPropagation();

    setIsDialogOpen(true);
  };

  const handleCloseDialog = (e: React.MouseEvent<HTMLButtonElement | HTMLDivElement>) => {
    e.stopPropagation();

    setIsDialogOpen(false);
  };

  const getTextFromChildren = () => {
    if (!contentRef.current) {
      return '';
    }

    return contentRef.current.textContent ?? '';
  };

  return (
    <>
      <Stack
        width="100%"
        direction="row"
        alignItems="flex-start"
        justifyContent="space-between"
        gap={1}
        overflow="hidden"
        textOverflow="ellipsis"
        onClick={handleOpenDialog}
        {...props}
        sx={{
          backgroundColor: 'action.hover',
          boxSizing: 'border-box',
          p: 1,
          borderRadius: 1,
          position: 'relative',
          transition: 'all 0.2s ease-in-out',

          '&:hover': {
            backgroundColor: 'action.selected',
            cursor: 'pointer',

            '#note-expand-button-icon': {
              transform: 'rotate(45deg) scale(1.1)',
            },
          },
          ...props.sx,
        }}
      >
        <Box
          ref={contentRef}
          sx={{
            boxSizing: 'border-box',
            flex: 1,
            maxHeight: 100,
            overflow: 'auto',
            whiteSpace: 'pre-wrap',
          }}
        >
          <Stack
            direction="row"
            alignItems="flex-start"
            justifyContent="space-between"
            sx={{
              minHeight: '100%',
            }}
          >
            <div style={{ flex: 1 }}>{children}</div>

            {!!visibility && (
              <Stack direction="row" gap={0.5} alignItems="center" justifyContent="flex-end">
                <MedicalNoteIcon isMedicalNote={visibility.isMedicalNote} />
                <PatientVisibilityIcon isVisibleToPatient={visibility.isVisibleToPatient} />
              </Stack>
            )}
          </Stack>
        </Box>

        {isOverflowing && (
          <IconButton
            title="View full note"
            size="small"
            onClick={handleOpenDialog}
            disableRipple
            sx={{
              position: 'absolute',
              bottom: 8,
              right: 4,
              backgroundColor: 'transparent',
              zIndex: 1,
            }}
          >
            <AllOutRoundedIcon
              id="note-expand-button-icon"
              sx={{
                fontSize: 16,
                transition: 'transform 0.2s ease-in-out',
              }}
            />
          </IconButton>
        )}
      </Stack>

      <Dialog
        open={isDialogOpen}
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
        onClick={(e) => e.stopPropagation()}
      >
        <DialogTitle>
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            Note Details
            <CopyButton textToCopy={getTextFromChildren} iconOnLeft>
              Copy Note
            </CopyButton>
          </Stack>
        </DialogTitle>
        <DialogContent>
          <div style={{ whiteSpace: 'pre-wrap' }}>{children}</div>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Close</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
